import { computed, Injectable, OnDestroy, inject, signal } from '@angular/core';
import { PluginListenerHandle } from '@capacitor/core';
import { Keyboard, KeyboardInfo, KeyboardResize, KeyboardStyle } from '@capacitor/keyboard';
import { LoggingService } from './logging.service';
import { PlatformService } from './platform.service';

@Injectable({ providedIn: 'root' })
export class KeyboardService implements OnDestroy {
  private readonly platform = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);
  private listeners: PluginListenerHandle[] = [];

  private readonly _isVisible = signal(false);
  private readonly _keyboardHeight = signal(0);

  private suppressUntil = 0;
  private suppressionHideRequest = 0;

  readonly isVisible = this._isVisible.asReadonly();

  readonly keyboardHeight = this._keyboardHeight.asReadonly();

  /**
   * Height the keyboard overlays on top of the viewport.
   * On Android with KeyboardResize.Ionic the viewport is already shrunk,
   * so the overlay height is 0. On iOS the keyboard sits on top of the
   * unchanged viewport, so the full height is returned.
   */
  readonly keyboardOverlayHeight = computed(() =>
    this.platform.isAndroid() ? 0 : this._keyboardHeight(),
  );

  constructor() {
    this.initialize();
  }

  private async initialize(): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      return;
    }

    try {
      const showListener = await Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
        if (Date.now() < this.suppressUntil && !this.isDialogFocused()) {
          Keyboard.hide().catch(() => {/* best-effort */});
          return;
        }
        this._isVisible.set(true);
        this._keyboardHeight.set(info.keyboardHeight);
        this.scrollFocusedElementIntoView();
      });
      this.listeners.push(showListener);

      const hideListener = await Keyboard.addListener('keyboardWillHide', () => {
        this._isVisible.set(false);
        this._keyboardHeight.set(0);
      });
      this.listeners.push(hideListener);

      await this.configureDefaults();
    } catch (error) {
      this.loggingService.logException(error, 'KeyboardService: Failed to initialize');
    }
  }

  private async configureDefaults(): Promise<void> {
    try {
      await Keyboard.setResizeMode({ mode: KeyboardResize.Ionic });

      if (this.platform.isIOS()) {
        await Keyboard.setStyle({ style: KeyboardStyle.Dark });
      }

      if (this.platform.isIOS()) {
        await Keyboard.setAccessoryBarVisible({ isVisible: true });
      }
    } catch (error) {
      this.loggingService.logException(error, 'KeyboardService: Failed to configure defaults');
    }
  }

  async hide(): Promise<void> {
    await this.hideInternal();
  }

  private async hideInternal(suppressionHideRequest?: number): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      return;
    }

    if (this.isDialogFocused()) {
      return;
    }

    try {
      const wasVisible = this._isVisible();

      // Call the native Keyboard.hide() BEFORE blurring the active element.
      // The native plugin uses activity.getCurrentFocus() to obtain a window
      // token for InputMethodManager.hideSoftInputFromWindow(). If we blur()
      // first, getCurrentFocus() returns null and the native call is a no-op,
      // meaning the keyboard dismiss animation never starts and the Keyboard
      // plugin never resets the FrameLayout height.
      await Keyboard.hide();

      if (
        suppressionHideRequest !== undefined &&
        suppressionHideRequest !== this.suppressionHideRequest
      ) {
        return;
      }

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      if (wasVisible) {
        await this.waitForKeyboardHidden(600);
      }
    } catch (error) {
      if (
        suppressionHideRequest !== undefined &&
        suppressionHideRequest !== this.suppressionHideRequest
      ) {
        return;
      }

      // Native hide may fail if there's no focused view; blur as fallback.
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      this.loggingService.logException(error, 'KeyboardService: Failed to hide keyboard');
    }
  }

  private waitForKeyboardHidden(timeoutMs: number): Promise<void> {
    return new Promise((resolve) => {
      if (!this._isVisible()) {
        resolve();
        return;
      }
      const start = Date.now();
      const check = () => {
        if (!this._isVisible() || Date.now() - start >= timeoutMs) {
          resolve();
          return;
        }
        setTimeout(check, 32);
      };
      setTimeout(check, 32);
    });
  }

  async show(): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      return;
    }

    try {
      await Keyboard.show();
    } catch (error) {
      this.loggingService.logException(error, 'KeyboardService: Failed to show keyboard');
    }
  }

  async setStyle(style: 'dark' | 'light'): Promise<void> {
    if (!this.platform.isIOS()) {
      return;
    }

    try {
      await Keyboard.setStyle({
        style: style === 'dark' ? KeyboardStyle.Dark : KeyboardStyle.Light,
      });
    } catch (error) {
      this.loggingService.logException(error, 'KeyboardService: Failed to set style');
    }
  }

  async setAccessoryBarVisible(visible: boolean): Promise<void> {
    if (!this.platform.isIOS()) {
      return;
    }

    try {
      await Keyboard.setAccessoryBarVisible({ isVisible: visible });
    } catch (error) {
      this.loggingService.logException(error, 'KeyboardService: Failed to set accessory bar visibility');
    }
  }

  async setScroll(disabled: boolean): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      return;
    }

    try {
      await Keyboard.setScroll({ isDisabled: disabled });
    } catch (error) {
      this.loggingService.logException(error, 'KeyboardService: Failed to set scroll behavior');
    }
  }

  async setResizeMode(mode: 'body' | 'ionic' | 'native' | 'none'): Promise<void> {
    if (!this.platform.isNativeMobile()) {
      return;
    }

    const modeMap: Record<string, KeyboardResize> = {
      body: KeyboardResize.Body,
      ionic: KeyboardResize.Ionic,
      native: KeyboardResize.Native,
      none: KeyboardResize.None,
    };

    try {
      await Keyboard.setResizeMode({ mode: modeMap[mode] });
    } catch (error) {
      this.loggingService.logException(error, 'KeyboardService: Failed to set resize mode');
    }
  }

  startSuppression(durationMs = 5_000): void {
    this.suppressUntil = Date.now() + durationMs;
    const suppressionHideRequest = ++this.suppressionHideRequest;
    void this.hideInternal(suppressionHideRequest);
  }

  stopSuppression(): void {
    this.suppressUntil = 0;
    this.suppressionHideRequest++;
  }

  private scrollFocusedElementIntoView(): void {
    setTimeout(() => {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        if (this.platform.isAndroid()) {
          // On Android, KeyboardResize.Ionic already resizes the viewport above
          // the keyboard. Delegate to the browser's native scroll behavior.
          activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          // On iOS, the viewport is NOT resized; the keyboard overlays content.
          // Manually calculate the visible area and scroll if needed.
          const accessoryBarHeight = 44;
          const keyboardTop = window.innerHeight - this._keyboardHeight() - accessoryBarHeight;
          const elementRect = activeElement.getBoundingClientRect();
          const elementBottom = elementRect.bottom;
          const scrollableParent = this.findScrollableParent(activeElement);

          if (elementBottom > keyboardTop) {
            const scrollAmount = elementBottom - keyboardTop + 20;
            if (scrollableParent) {
              scrollableParent.scrollBy({ top: scrollAmount, behavior: 'smooth' });
            } else {
              window.scrollBy({ top: scrollAmount, behavior: 'smooth' });
            }
          }
        }
      }
    }, 100);
  }

  private findScrollableParent(element: HTMLElement): HTMLElement | null {
    let parent = element.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight
      ) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  private isDialogFocused(): boolean {
    const activeElement = document.activeElement;
    return activeElement instanceof HTMLElement && activeElement.closest('[role="dialog"]') !== null;
  }

  ngOnDestroy(): void {
    this.listeners.forEach((listener) => listener.remove());
  }
}
